import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Plus, Check } from "lucide-react";
import { useTranslation } from "react-i18next";

// Common brokers for German / EU users (plus popular international).
const PRESETS = [
  { name: "Trade Republic", color: "#111827" },
  { name: "Scalable Capital", color: "#0a7d3b" },
  { name: "Comdirect", color: "#f5c518" },
  { name: "ING", color: "#ff6200" },
  { name: "DKB", color: "#004b8d" },
  { name: "Consorsbank", color: "#e2001a" },
  { name: "Flatex", color: "#0067b1" },
  { name: "S Broker (Sparkasse)", color: "#e2001a" },
  { name: "Bison", color: "#f04923" },
  { name: "Revolut", color: "#0666eb" },
  { name: "Coinbase", color: "#0052ff" },
  { name: "eToro", color: "#13c636" },
  { name: "Interactive Brokers", color: "#d81222" },
];

function Avatar({ name, color }) {
  return (
    <span className="w-6 h-6 rounded-md flex items-center justify-center text-white text-[11px] font-semibold shrink-0" style={{ background: color || "#0052FF" }}>
      {name.replace(/[^A-Za-z]/g, "").slice(0, 1).toUpperCase() || "?"}
    </span>
  );
}

export default function BrokerSelect({ value, onChange, brokers = [], testid = "broker-select" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef();

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // If there's only one broker to choose from (e.g. the only one holding this
  // ticker, or the only one the user has ever used), just pick it — don't make
  // the user click through a dropdown with a single option.
  useEffect(() => {
    if (brokers.length === 1 && !value) onChange(brokers[0]);
  }, [brokers]);

  const presetNames = PRESETS.map((p) => p.name);
  const merged = [
    ...PRESETS,
    ...brokers.filter((b) => b && !presetNames.includes(b)).map((b) => ({ name: b, color: "#64748b" })),
  ];
  const q = query.trim().toLowerCase();
  const filtered = q ? merged.filter((m) => m.name.toLowerCase().includes(q)) : merged;
  const showAddCustom = q && !merged.some((m) => m.name.toLowerCase() === q);

  const choose = (n) => { onChange(n); setOpen(false); setQuery(""); };
  const current = merged.find((m) => m.name === value);

  return (
    <div ref={ref} className="relative">
      <button type="button" data-testid={testid} onClick={() => setOpen((o) => !o)} className="input flex items-center justify-between text-left">
        <span className="flex items-center gap-2 min-w-0">
          {value ? <Avatar name={value} color={current?.color} /> : null}
          <span className={value ? "text-slate-900 truncate" : "text-slate-400"}>{value || t("dialogs.selectBroker")}</span>
        </span>
        <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }} transition={{ duration: 0.15 }}
            className="absolute z-50 mt-2 w-full bg-white rounded-2xl border border-slate-200/70 shadow-soft overflow-hidden"
          >
            <div className="p-2 border-b border-slate-100">
              <input autoFocus data-testid="broker-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("dialogs.searchBroker")} className="input py-2 text-sm" />
            </div>
            <div className="max-h-56 overflow-y-auto py-1">
              {filtered.map((m) => (
                <button type="button" key={m.name} data-testid={`broker-option-${m.name}`} onClick={() => choose(m.name)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-brand-50 transition-colors text-left text-sm">
                  <Avatar name={m.name} color={m.color} />
                  <span className="flex-1 truncate">{m.name}</span>
                  {value === m.name && <Check size={15} className="text-brand-500" />}
                </button>
              ))}
              {showAddCustom && (
                <button type="button" data-testid="broker-add-custom" onClick={() => choose(query.trim())}
                  className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-brand-50 transition-colors text-left text-sm text-brand-600 font-medium">
                  <Plus size={16} /> {t("dialogs.add")} "{query.trim()}"
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
